import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserRound } from 'lucide-react';
import { type User } from '@bliks-io/contracts';
import {
  EntityPickerHighlightedText,
  UserCard,
  UserName,
  toUserCardProps,
  getUserFullName,
  type EntityPickerConfig,
  type EntityPickerCopy,
  type EntityPickerRowMeta,
  type EntityPickerSearchKey
} from '@bliks-io/ui';
import { useOrgMembers } from '@/api/queries/useOrgMembers';
import { useDepartments } from '@/api/queries/useDepartments';
import { useUserPickerSuggestions } from '@/api/queries/usePickerSuggestions';
import { buildEntityPickerCopy } from '../buildCopy';
import { EntityPickerBucketsList } from '../shared/EntityPickerBucketsList';
import {
  buildBucketSections,
  buildRowMetaIndex,
  shouldShowBucketView
} from '../shared/buildBucketSections';
import { buildDepartmentFilter, buildDepartmentOptions } from '../shared/buildDepartmentOptions';
import { buildPersonBucketTitles } from '../shared/buildPersonBucketTitles';

// Module-level so the reference is stable across renders — useEntityFuzzySearch
// relies on reference equality of `keys` to avoid rebuilding the Fuse index.
const SEARCH_KEYS: EntityPickerSearchKey<User>[] = [
  { name: 'firstName', weight: 0.3 },
  { name: 'lastName', weight: 0.3 },
  { name: 'email', weight: 0.1 },
  { name: 'department.name', weight: 0.15 },
  { name: 'role.name', weight: 0.15 }
];

export interface UsePersonPickerConfigArgs {
  open: boolean;
  mode: 'single' | 'multi';
  processId?: string;
  recordingId?: string;
  /**
   * Per-context copy overrides (title/description). The base `user` picker copy
   * is tuned for the helpers context; the "who else does this" picker reuses the
   * same person list but needs its own framing.
   */
  copyOverrides?: Partial<EntityPickerCopy>;
}

export interface UsePersonPickerConfigResult {
  items: User[];
  isLoading: boolean;
  config: EntityPickerConfig<User>;
}

export function usePersonPickerConfig({
  open,
  mode,
  processId,
  recordingId,
  copyOverrides
}: UsePersonPickerConfigArgs): UsePersonPickerConfigResult {
  const { t } = useTranslation();
  // Internal only: this picker names who PERFORMS a step. External members sit
  // outside the org — they moderate as process owner or record on a contact's
  // behalf, but they are not part of a process. Picking one here would also
  // make them survey-eligible (`assignedToModel: 'User'` is not covered by
  // SURVEY_EXEMPT_ASSIGNEE_MODELS), putting non-org effort into Prozesskosten.
  const { data: users = [], isLoading } = useOrgMembers({ classification: 'internal' });
  const { data: departments = [] } = useDepartments();
  const { data: suggestions, isLoading: isLoadingSuggestions } = useUserPickerSuggestions({
    processId,
    recordingId,
    enabled: open
  });
  const [departmentFilter, setDepartmentFilter] = useState<string | null>(null);

  const departmentOptions = useMemo(() => buildDepartmentOptions(departments), [departments]);

  const userList = users as User[];
  const byId = useMemo(() => new Map(userList.map((u) => [u._id, u] as const)), [userList]);

  const rowMetaById = useMemo(() => buildRowMetaIndex(suggestions), [suggestions]);

  const config = useMemo<EntityPickerConfig<User>>(() => {
    const baseCopy = buildEntityPickerCopy(t, 'user');
    return {
      headerIcon: <UserRound className="h-4 w-4" />,
      searchKeys: SEARCH_KEYS,
      matchInField: {
        primaryField: 'firstName',
        fieldLabels: {
          email: t('entityPicker.common.fields.email'),
          'department.name': t('entityPicker.common.fields.department'),
          'role.name': t('entityPicker.common.fields.role'),
          lastName: t('entityPicker.common.fields.name')
        }
      },
      departmentFilter: buildDepartmentFilter(
        t,
        departmentOptions,
        departmentFilter,
        setDepartmentFilter
      ),
      getRowMeta: (item): EntityPickerRowMeta => rowMetaById.get(item._id) ?? {},
      getChipName: (item) => getUserFullName(item.firstName, item.lastName),
      renderRow: (item, ctx) => (
        <UserCard
          user={toUserCardProps(item)}
          variant="with-role"
          itemVariant="default"
          size="xs"
          titleSlot={
            // The title slot replaces the card's own suffix, so UserName
            // wraps the highlighted name and re-adds it, muted.
            <UserName
              name={
                <EntityPickerHighlightedText
                  text={getUserFullName(item.firstName, item.lastName)}
                  field="firstName"
                  matches={ctx.matches}
                />
              }
              user={item}
            />
          }
        />
      ),
      bucketing: {
        isLoading: isLoadingSuggestions,
        hasBuckets: (candidates) => shouldShowBucketView(suggestions, candidates),
        renderEmptyQueryView: ({ items, selectedIds: draft, toggleId }) => {
          const subset = new Map(items.map((u) => [u._id, u] as const));
          const filteredById = new Map<string, User>();
          for (const [id, u] of subset) if (byId.has(id)) filteredById.set(id, u);
          const sections = buildBucketSections<User>({
            byId: filteredById,
            suggestions,
            t,
            config: buildPersonBucketTitles(t)
          });
          return (
            <EntityPickerBucketsList<User>
              sections={sections}
              selectedIds={draft}
              onToggle={toggleId}
              mode={mode}
              renderItem={(item) => (
                <UserCard
                  user={toUserCardProps(item)}
                  variant="with-role"
                  itemVariant="default"
                  size="xs"
                />
              )}
            />
          );
        }
      },
      copy: { ...baseCopy, ...copyOverrides }
    };
  }, [
    t,
    suggestions,
    isLoadingSuggestions,
    byId,
    rowMetaById,
    departmentOptions,
    departmentFilter,
    mode,
    copyOverrides
  ]);

  return { items: userList, isLoading, config };
}
